import {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {css, Theme} from "@emotion/react";

import {calculateRemSize, flex, mb, onDesktop, p, w100} from "@pg-design/helpers-css";
import {EyeIcon, FiltersIcon, SortIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {FILTERS_TEST_IDS, NAVIGATION_TEST_IDS} from "@pg-mono/e2e-test-utils/src/rp/common/test_id";
import {toQueryValues} from "@pg-mono/form";
import {isEqual, omit} from "@pg-mono/nodash";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {useHomeMatchPreferencesStorage} from "../../../../application/hooks/use_home_match_preferences_storage";
import {DesktopBlend} from "../../../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../../../atoms/MobileOptionsModal";
import {useClickOutside} from "../../../../hooks/handle_click_outside";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues, PropertyListFilters} from "../../../../property_list/PropertyListFilters";
import {PropertyListTabSwitcher, PropertyListType, TabSwitcherStyledButton} from "../../../../property_list/PropertyListTabSwitcher";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {searchPropertyListHit} from "../../../../tracking/algolytics_hits/search_property_list_hit";
import {gtmEventPropertyListSearch} from "../../../../tracking/google_tag_manager/gtm_event_ui";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE, OFFER_DETAIL_PROPERTY_LIST_TILES_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {SortOptionsValues} from "../../types/SortOptionsValues";
import {Sort} from "../sort/Sort";
import {SortOptions} from "../sort/SortOptions";
import {initialPropertiesFilterValues} from "./OfferDetailPropertyList";
import {propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";

interface IProps {
    propertyListObj: IPropertyListObj;
    filterValues: ISearchFiltersFormValues;
    offerId: number;
    offer: IOfferDetail;
    setFilterValues: (filterValues: ISearchFiltersFormValues) => void;
    sortOptions: {label: string; value: number; slug: string}[];
    setCurrentPropertyListType: (type: PropertyListType) => void;
    currentPropertyListType: PropertyListType;
}
export const OfferDetailPropertyListFilters = (props: IProps) => {
    const {filterValues, setFilterValues, sortOptions, currentPropertyListType, setCurrentPropertyListType} = props;
    const {isMobile} = useUserDevice();

    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [isDesktopFilterBlendActive, setDesktopFilterBlendActive] = useState(false);
    const [isDesktopSortBlendActive, setDesktopSortBlendActive] = useState(false);
    const [isMobileFiltersActive, setMobileFiltersActive] = useState(false);
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    // TODO: we should use sort from `propertyListObj` instead of relying on local state
    const [sortValue, setSortValue] = useState(0);

    const latestFetchValues = useRef<ISearchFiltersFormValues>(filterValues);
    const sortRef = useRef<HTMLElement | null>(null);
    const prevFilterState = useRef<ISearchFiltersFormValues | null>(null);
    const latestFetchValuesReinitialized = useRef<boolean>(false);

    const offerType = props.offer.type;

    const queryValues = {sort: props.propertyListObj.sort.currentSort, page: 1};
    const dispatch = useDispatch();

    const sortRefHandle = (element: HTMLElement | null) => {
        sortRef.current = element;
    };

    const {putPreferences} = useHomeMatchPreferencesStorage();

    useClickOutside(sortRef, () => {
        setDropdownOpen(false);
        setDesktopFilterBlendActive(false);
        setDesktopSortBlendActive(false);
    });

    /*
        mobile modals handling
    */
    const toggleMobileFilterModal = () =>
        setMobileFiltersActive((prev) => {
            prevFilterState.current = prev
                ? null
                : {
                      rooms: filterValues.rooms,
                      price: filterValues.price,
                      area: filterValues.area,
                      floor_choices: filterValues.floor_choices,
                      house_storeys: filterValues.house_storeys
                  };
            return !prev;
        });
    const toggleMobileSortModal = () => {
        setMobileSortActive((prev) => {
            return !prev;
        });
    };
    const discardModal = () => {
        closeMobileModal();
        if (prevFilterState.current != null) {
            setFilterValues(prevFilterState.current);
        }
        if (sortValue !== 0) {
            setSortValue(sortValue);
        }
    };

    const closeMobileModal = () => {
        if (isMobileFiltersActive) {
            setMobileFiltersActive(false);
        } else {
            setMobileSortActive(false);
        }
    };

    /*
        filters handling logic
    */
    useEffect(() => {
        const filtersHasChange = !isEqual(latestFetchValues.current, filterValues);

        if (filtersHasChange && !latestFetchValuesReinitialized.current) {
            latestFetchValues.current = {...filterValues};

            latestFetchValuesReinitialized.current = true;
        }
    }, [filterValues]);

    const fetchFilteredProperties = async <T extends keyof ISearchFiltersFormValues>(name?: T, value?: ISearchFiltersFormValues[T]) => {
        //check for changes values in old redux form
        const filtersHasChange = !isEqual(latestFetchValues.current, filterValues);

        if (name && value && isEqual(latestFetchValues.current[name], value) && !filtersHasChange) {
            // prevent desktop from fetching data on every click that opens single field
            return;
        }
        if (name === "rooms" && props.offer.region.country === Country.POLAND) {
            putPreferences({rooms: value as ISearchFiltersFormValues["rooms"]});
        }
        // gtm
        !isEqual(latestFetchValues.current.rooms, filterValues.rooms) && gtmEventPropertyListSearch("pokoje");
        !isEqual(latestFetchValues.current.area, filterValues.area) && gtmEventPropertyListSearch("powierzchnia");
        !isEqual(latestFetchValues.current.price, filterValues.price) && gtmEventPropertyListSearch("cena");

        const newFilters = {...filterValues};

        if (name && value && filterValues[name] !== undefined) {
            newFilters[name] = value;
        }

        const filterQueryValues = toQueryValues(propertyListTableFiltersFields, newFilters);

        if (props.offerId) {
            latestFetchValues.current = newFilters;
            const properties = await fetchOfferDetailProperties(
                props.offerId,
                PropertyListTabTypes.FILTER,
                OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                queryValues,
                filterQueryValues
            )(dispatch);

            dispatch(fetchOfferDetailLowestPriceProperty(props.offerId, filterQueryValues));

            if (properties) {
                searchPropertyListHit(
                    omit(newFilters, ["floor_choices", "house_storeys"]),
                    properties.map((el) => el.id),
                    props.offerId
                );
            }
        }

        if (props.propertyListObj.tab.currentTab !== PropertyListTabTypes.FILTER) {
            props.propertyListObj.tab.setCurrentTab(PropertyListTabTypes.FILTER);
        }
    };

    const updateFiltersOnChange = (filter: Partial<ISearchFiltersFormValues>) => {
        const newFilters = {...filterValues, ...filter};
        setFilterValues(newFilters);
    };

    const resetFilters = () => {
        setFilterValues(initialPropertiesFilterValues);
        if (props.offerId) {
            fetchOfferDetailProperties(props.offerId, PropertyListTabTypes.FILTER, OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE, {
                sort: props.propertyListObj.sort.currentSort,
                page: 1
            })(dispatch);

            dispatch(fetchOfferDetailLowestPriceProperty(props.offerId));
        }
        props.propertyListObj.tab.setCurrentTab(PropertyListTabTypes.FILTER);

        closeMobileModal();
    };

    /*
        sort handling logic
    */

    const clearSort = () => {
        setSortValue(0);
        if (isMobile) {
            setMobileSortActive(false);
        }
        props.propertyListObj.onSortChange(null);
        setDropdownOpen(false);
        setDesktopSortBlendActive(false);
    };

    const saveSort = () => {
        if (sortValue === SortOptionsValues.NOT_SELECTED) {
            props.propertyListObj.onSortChange(null, toQueryValues(propertyListTableFiltersFields, filterValues));
        } else {
            const sort = sortOptions.filter((el) => {
                return el.value === sortValue;
            });
            props.propertyListObj.onSortChange(sort[0].slug, toQueryValues(propertyListTableFiltersFields, filterValues));
        }
        if (isMobile) {
            setMobileSortActive(false);
        }
        setDropdownOpen(false);
        setDesktopSortBlendActive(false);
    };

    /*
        tab changing logic
    */
    const switchCurrentPropertyListType = async (type: PropertyListType) => {
        if (type !== currentPropertyListType) {
            const isTableTab = type === PropertyListType.TABLE;
            await fetchOfferDetailProperties(
                props.offerId,
                PropertyListTabTypes.FILTER,
                isTableTab ? OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE : OFFER_DETAIL_PROPERTY_LIST_TILES_DEFAULT_PAGE_SIZE,
                queryValues,
                {...toQueryValues(propertyListTableFiltersFields, filterValues)}
            )(dispatch);
            setCurrentPropertyListType(type);
            type === PropertyListType.TILES && showPropertyPlanView(ShowPropertyPlanView.PLAN_DEFAULT, props.offer, props.offer.vendor);
            saveSort();
        }
    };

    /*
        UseEffect below is responsible for sorting using the header from the PropertyListTable.tsx
     */
    useEffect(() => {
        const slug = props.propertyListObj.sort.currentSort;
        const currentSort = sortOptions.find((el) => el.value === sortValue);
        if ((!!currentSort && currentSort.slug !== slug) || sortValue === SortOptionsValues.NOT_SELECTED) {
            const newSort = sortOptions.find((el) => el.slug === slug);
            setSortValue(!!newSort ? newSort.value : sortValue);
        }
    }, [props.propertyListObj.sort.currentSort]);

    return (
        <>
            <DesktopBlend isActive={isDesktopFilterBlendActive || isDesktopSortBlendActive} isFullScreen />

            <div ref={sortRefHandle} css={wrapper}>
                {props.propertyListObj.tab.currentTab !== PropertyListTabTypes.INVESTMENT && (
                    <>
                        <div css={desktopWrapper}>
                            <PropertyListFilters
                                values={filterValues}
                                availableRooms={props.offer.stats?.rooms}
                                onChange={updateFiltersOnChange}
                                onAfterChange={fetchFilteredProperties}
                                onMobileCloseModal={closeMobileModal}
                                onMobileResetForm={resetFilters}
                                setDesktopFilterBlendActive={setDesktopFilterBlendActive}
                                offerType={offerType}
                                css={desktopFilters}
                                currency={props.offer.currency}
                                maxPriceInOffer={props.offer.stats?.ranges_price_max}
                            />
                        </div>
                    </>
                )}
                <>
                    <div css={wrapperStyles}>
                        {!isMobile && (
                            <InputWrapper css={sortWrapper}>
                                <Sort
                                    isDropdownOpen={isDropdownOpen}
                                    setDropdownOpen={setDropdownOpen}
                                    isMobile={isMobile as boolean}
                                    setDesktopFilterBlendActive={setDesktopSortBlendActive}
                                    currentValue={sortValue}
                                    setCurrentValue={setSortValue}
                                    sortOptions={sortOptions}
                                >
                                    <SortOptions
                                        currentValue={sortValue}
                                        setCurrentValue={setSortValue}
                                        sortOptions={sortOptions}
                                        onCancel={clearSort}
                                        onSave={saveSort}
                                    />
                                </Sort>
                            </InputWrapper>
                        )}
                        <div css={listHeaderStyle}>
                            <PropertyListTabSwitcher
                                currentPropertyListType={currentPropertyListType}
                                setCurrentPropertyListType={switchCurrentPropertyListType}
                                buttonCss={tabSwitcherButtonStyle}
                                css={tabSwitcherStyle}
                            />
                            <Planner3dModal vendorId={props.offer.vendor.id} offer={props.offer} css={filtersPlanner3D} routedModalId="propertyList">
                                <TabSwitcherStyledButton variant="none_secondary" size="medium" iconLeft={EyeIcon} isActive>
                                    3D
                                </TabSwitcherStyledButton>
                            </Planner3dModal>
                            {props.propertyListObj.tab.currentTab !== PropertyListTabTypes.INVESTMENT && isMobile && (
                                <>
                                    <FiltersIcon
                                        onClick={toggleMobileFilterModal}
                                        size="2.4"
                                        wrapperSize="2.4"
                                        wrapperColor="transparent"
                                        css={{marginLeft: "auto"}}
                                        dataTestid={FILTERS_TEST_IDS.FILTER_MOBILE_ICON}
                                    />

                                    <MobileOptionsModal modalHeading="Filtry" isOpen={isMobileFiltersActive} onModalClose={discardModal}>
                                        <PropertyListFilters
                                            isMobile
                                            values={filterValues}
                                            onChange={updateFiltersOnChange}
                                            onAfterChange={fetchFilteredProperties}
                                            onMobileCloseModal={closeMobileModal}
                                            onMobileResetForm={resetFilters}
                                            setDesktopFilterBlendActive={setDesktopFilterBlendActive}
                                            currency={props.offer.currency}
                                            maxPriceInOffer={props.offer.stats?.ranges_price_max}
                                            offerType={offerType}
                                        />
                                    </MobileOptionsModal>
                                </>
                            )}
                            {isMobile && (
                                <>
                                    <SortIcon
                                        onClick={toggleMobileSortModal}
                                        size="2.4"
                                        wrapperSize="2.4"
                                        wrapperColor="transparent"
                                        dataTestid={NAVIGATION_TEST_IDS.SORT_ICON}
                                    />

                                    <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={discardModal}>
                                        <SortOptions
                                            currentValue={sortValue}
                                            setCurrentValue={setSortValue}
                                            sortOptions={sortOptions}
                                            onCancel={clearSort}
                                            onSave={saveSort}
                                        />
                                    </MobileOptionsModal>
                                </>
                            )}
                        </div>
                    </div>
                </>
            </div>
        </>
    );
};

const desktopWrapper = (props: Theme) => css`
    justify-items: stretch;
    display: none;

    ${mb(3)};

    @media (min-width: ${props.breakpoints.md}) {
        display: flex;
    }

    @media (min-width: ${props.breakpoints.lg}) {
        width: 100%;
        ${mb(0)};
    }
`;

const wrapperStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;

    ${onDesktop(css`
        flex-direction: row;
    `)}
`;

export const sortWrapper = (theme: Theme) => css`
    ${p(0)};

    ${onDesktop(css`
        flex: 0 0 20%;
    `)};

    @media (min-width: ${theme.breakpoints.lg}) {
        min-width: 200px;
    }
`;

const wrapper = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.lg}) {
        ${flex("center")};
        gap: ${calculateRemSize(1.5)};
    }
`;

const desktopFilters = css`
    width: 100%;
`;

const listHeaderStyle = (theme: Theme) => css`
    ${flex("center", "space-between")};
    gap: ${calculateRemSize(3)};
    ${w100};

    @media (min-width: ${theme.breakpoints.md}) {
        width: initial;
        flex-basis: initial;
        gap: 0;
    }
`;

const tabSwitcherButtonStyle = (theme: Theme) => css`
    ${p(2, 0.5)};
    height: initial;

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(2, 4)};
    }
`;

const tabSwitcherStyle = (theme: Theme) => css`
    gap: ${calculateRemSize(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        gap: 0;
    }
`;

const filtersPlanner3D = (theme: Theme) => css`
    display: none;
    @media (min-width: ${theme.breakpoints.md}) {
        display: inline-flex;
    }
`;
import {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {css, Theme} from "@emotion/react";

import {calculateRemSize, flex, mb, onDesktop, p, w100} from "@pg-design/helpers-css";
import {EyeIcon, FiltersIcon, SortIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {FILTERS_TEST_IDS, NAVIGATION_TEST_IDS} from "@pg-mono/e2e-test-utils/src/rp/common/test_id";
import {toQueryValues} from "@pg-mono/form";
import {isEqual, omit} from "@pg-mono/nodash";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {useHomeMatchPreferencesStorage} from "../../../../application/hooks/use_home_match_preferences_storage";
import {DesktopBlend} from "../../../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../../../atoms/MobileOptionsModal";
import {useClickOutside} from "../../../../hooks/handle_click_outside";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues, PropertyListFilters} from "../../../../property_list/PropertyListFilters";
import {PropertyListTabSwitcher, PropertyListType, TabSwitcherStyledButton} from "../../../../property_list/PropertyListTabSwitcher";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {searchPropertyListHit} from "../../../../tracking/algolytics_hits/search_property_list_hit";
import {gtmEventPropertyListSearch} from "../../../../tracking/google_tag_manager/gtm_event_ui";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE, OFFER_DETAIL_PROPERTY_LIST_TILES_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {SortOptionsValues} from "../../types/SortOptionsValues";
import {Sort} from "../sort/Sort";
import {SortOptions} from "../sort/SortOptions";
import {initialPropertiesFilterValues} from "./OfferDetailPropertyList";
import {propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";

interface IProps {
    propertyListObj: IPropertyListObj;
    filterValues: ISearchFiltersFormValues;
    offerId: number;
    offer: IOfferDetail;
    setFilterValues: (filterValues: ISearchFiltersFormValues) => void;
    sortOptions: {label: string; value: number; slug: string}[];
    setCurrentPropertyListType: (type: PropertyListType) => void;
    currentPropertyListType: PropertyListType;
}
export const OfferDetailPropertyListFilters = (props: IProps) => {
    const {filterValues, setFilterValues, sortOptions, currentPropertyListType, setCurrentPropertyListType} = props;
    const {isMobile} = useUserDevice();

    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [isDesktopFilterBlendActive, setDesktopFilterBlendActive] = useState(false);
    const [isDesktopSortBlendActive, setDesktopSortBlendActive] = useState(false);
    const [isMobileFiltersActive, setMobileFiltersActive] = useState(false);
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    // TODO: we should use sort from `propertyListObj` instead of relying on local state
    const [sortValue, setSortValue] = useState(0);

    const latestFetchValues = useRef<ISearchFiltersFormValues>(filterValues);
    const sortRef = useRef<HTMLElement | null>(null);
    const prevFilterState = useRef<ISearchFiltersFormValues | null>(null);
    const latestFetchValuesReinitialized = useRef<boolean>(false);

    const offerType = props.offer.type;

    const queryValues = {sort: props.propertyListObj.sort.currentSort, page: 1};
    const dispatch = useDispatch();

    const sortRefHandle = (element: HTMLElement | null) => {
        sortRef.current = element;
    };

    const {putPreferences} = useHomeMatchPreferencesStorage();

    useClickOutside(sortRef, () => {
        setDropdownOpen(false);
        setDesktopFilterBlendActive(false);
        setDesktopSortBlendActive(false);
    });

    /*
        mobile modals handling
    */
    const toggleMobileFilterModal = () =>
        setMobileFiltersActive((prev) => {
            prevFilterState.current = prev
                ? null
                : {
                      rooms: filterValues.rooms,
                      price: filterValues.price,
                      area: filterValues.area,
                      floor_choices: filterValues.floor_choices,
                      house_storeys: filterValues.house_storeys
                  };
            return !prev;
        });
    const toggleMobileSortModal = () => {
        setMobileSortActive((prev) => {
            return !prev;
        });
    };
    const discardModal = () => {
        closeMobileModal();
        if (prevFilterState.current != null) {
            setFilterValues(prevFilterState.current);
        }
        if (sortValue !== 0) {
            setSortValue(sortValue);
        }
    };

    const closeMobileModal = () => {
        if (isMobileFiltersActive) {
            setMobileFiltersActive(false);
        } else {
            setMobileSortActive(false);
        }
    };

    /*
        filters handling logic
    */
    useEffect(() => {
        const filtersHasChange = !isEqual(latestFetchValues.current, filterValues);

        if (filtersHasChange && !latestFetchValuesReinitialized.current) {
            latestFetchValues.current = {...filterValues};

            latestFetchValuesReinitialized.current = true;
        }
    }, [filterValues]);

    const fetchFilteredProperties = async <T extends keyof ISearchFiltersFormValues>(name?: T, value?: ISearchFiltersFormValues[T]) => {
        //check for changes values in old redux form
        const filtersHasChange = !isEqual(latestFetchValues.current, filterValues);

        if (name && value && isEqual(latestFetchValues.current[name], value) && !filtersHasChange) {
            // prevent desktop from fetching data on every click that opens single field
            return;
        }
        if (name === "rooms" && props.offer.region.country === Country.POLAND) {
            putPreferences({rooms: value as ISearchFiltersFormValues["rooms"]});
        }
        // gtm
        !isEqual(latestFetchValues.current.rooms, filterValues.rooms) && gtmEventPropertyListSearch("pokoje");
        !isEqual(latestFetchValues.current.area, filterValues.area) && gtmEventPropertyListSearch("powierzchnia");
        !isEqual(latestFetchValues.current.price, filterValues.price) && gtmEventPropertyListSearch("cena");

        const newFilters = {...filterValues};

        if (name && value && filterValues[name] !== undefined) {
            newFilters[name] = value;
        }

        const filterQueryValues = toQueryValues(propertyListTableFiltersFields, newFilters);

        if (props.offerId) {
            latestFetchValues.current = newFilters;
            const properties = await fetchOfferDetailProperties(
                props.offerId,
                PropertyListTabTypes.FILTER,
                OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                queryValues,
                filterQueryValues
            )(dispatch);

            dispatch(fetchOfferDetailLowestPriceProperty(props.offerId, filterQueryValues));

            if (properties) {
                searchPropertyListHit(
                    omit(newFilters, ["floor_choices", "house_storeys"]),
                    properties.map((el) => el.id),
                    props.offerId
                );
            }
        }

        if (props.propertyListObj.tab.currentTab !== PropertyListTabTypes.FILTER) {
            props.propertyListObj.tab.setCurrentTab(PropertyListTabTypes.FILTER);
        }
    };

    const updateFiltersOnChange = (filter: Partial<ISearchFiltersFormValues>) => {
        const newFilters = {...filterValues, ...filter};
        setFilterValues(newFilters);
    };

    const resetFilters = () => {
        setFilterValues(initialPropertiesFilterValues);
        if (props.offerId) {
            fetchOfferDetailProperties(props.offerId, PropertyListTabTypes.FILTER, OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE, {
                sort: props.propertyListObj.sort.currentSort,
                page: 1
            })(dispatch);

            dispatch(fetchOfferDetailLowestPriceProperty(props.offerId));
        }
        props.propertyListObj.tab.setCurrentTab(PropertyListTabTypes.FILTER);

        closeMobileModal();
    };

    /*
        sort handling logic
    */

    const clearSort = () => {
        setSortValue(0);
        if (isMobile) {
            setMobileSortActive(false);
        }
        props.propertyListObj.onSortChange(null);
        setDropdownOpen(false);
        setDesktopSortBlendActive(false);
    };

    const saveSort = () => {
        if (sortValue === SortOptionsValues.NOT_SELECTED) {
            props.propertyListObj.onSortChange(null, toQueryValues(propertyListTableFiltersFields, filterValues));
        } else {
            const sort = sortOptions.filter((el) => {
                return el.value === sortValue;
            });
            props.propertyListObj.onSortChange(sort[0].slug, toQueryValues(propertyListTableFiltersFields, filterValues));
        }
        if (isMobile) {
            setMobileSortActive(false);
        }
        setDropdownOpen(false);
        setDesktopSortBlendActive(false);
    };

    /*
        tab changing logic
    */
    const switchCurrentPropertyListType = async (type: PropertyListType) => {
        if (type !== currentPropertyListType) {
            const isTableTab = type === PropertyListType.TABLE;
            await fetchOfferDetailProperties(
                props.offerId,
                PropertyListTabTypes.FILTER,
                isTableTab ? OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE : OFFER_DETAIL_PROPERTY_LIST_TILES_DEFAULT_PAGE_SIZE,
                queryValues,
                {...toQueryValues(propertyListTableFiltersFields, filterValues)}
            )(dispatch);
            setCurrentPropertyListType(type);
            type === PropertyListType.TILES && showPropertyPlanView(ShowPropertyPlanView.PLAN_DEFAULT, props.offer, props.offer.vendor);
            saveSort();
        }
    };

    /*
        UseEffect below is responsible for sorting using the header from the PropertyListTable.tsx
     */
    useEffect(() => {
        const slug = props.propertyListObj.sort.currentSort;
        const currentSort = sortOptions.find((el) => el.value === sortValue);
        if ((!!currentSort && currentSort.slug !== slug) || sortValue === SortOptionsValues.NOT_SELECTED) {
            const newSort = sortOptions.find((el) => el.slug === slug);
            setSortValue(!!newSort ? newSort.value : sortValue);
        }
    }, [props.propertyListObj.sort.currentSort]);

    return (
        <>
            <DesktopBlend isActive={isDesktopFilterBlendActive || isDesktopSortBlendActive} isFullScreen />

            <div ref={sortRefHandle} css={wrapper}>
                {props.propertyListObj.tab.currentTab !== PropertyListTabTypes.INVESTMENT && (
                    <>
                        <div css={desktopWrapper}>
                            <PropertyListFilters
                                values={filterValues}
                                availableRooms={props.offer.stats?.rooms}
                                onChange={updateFiltersOnChange}
                                onAfterChange={fetchFilteredProperties}
                                onMobileCloseModal={closeMobileModal}
                                onMobileResetForm={resetFilters}
                                setDesktopFilterBlendActive={setDesktopFilterBlendActive}
                                offerType={offerType}
                                css={desktopFilters}
                                currency={props.offer.currency}
                                maxPriceInOffer={props.offer.stats?.ranges_price_max}
                            />
                        </div>
                    </>
                )}
                <>
                    <div css={wrapperStyles}>
                        {!isMobile && (
                            <InputWrapper css={sortWrapper}>
                                <Sort
                                    isDropdownOpen={isDropdownOpen}
                                    setDropdownOpen={setDropdownOpen}
                                    isMobile={isMobile as boolean}
                                    setDesktopFilterBlendActive={setDesktopSortBlendActive}
                                    currentValue={sortValue}
                                    setCurrentValue={setSortValue}
                                    sortOptions={sortOptions}
                                >
                                    <SortOptions
                                        currentValue={sortValue}
                                        setCurrentValue={setSortValue}
                                        sortOptions={sortOptions}
                                        onCancel={clearSort}
                                        onSave={saveSort}
                                    />
                                </Sort>
                            </InputWrapper>
                        )}
                        <div css={listHeaderStyle}>
                            <PropertyListTabSwitcher
                                currentPropertyListType={currentPropertyListType}
                                setCurrentPropertyListType={switchCurrentPropertyListType}
                                buttonCss={tabSwitcherButtonStyle}
                                css={tabSwitcherStyle}
                            />
                            <Planner3dModal vendorId={props.offer.vendor.id} offer={props.offer} css={filtersPlanner3D} routedModalId="propertyList">
                                <TabSwitcherStyledButton variant="none_secondary" size="medium" iconLeft={EyeIcon} isActive>
                                    3D
                                </TabSwitcherStyledButton>
                            </Planner3dModal>
                            {props.propertyListObj.tab.currentTab !== PropertyListTabTypes.INVESTMENT && isMobile && (
                                <>
                                    <FiltersIcon
                                        onClick={toggleMobileFilterModal}
                                        size="2.4"
                                        wrapperSize="2.4"
                                        wrapperColor="transparent"
                                        css={{marginLeft: "auto"}}
                                        dataTestid={FILTERS_TEST_IDS.FILTER_MOBILE_ICON}
                                    />

                                    <MobileOptionsModal modalHeading="Filtry" isOpen={isMobileFiltersActive} onModalClose={discardModal}>
                                        <PropertyListFilters
                                            isMobile
                                            values={filterValues}
                                            onChange={updateFiltersOnChange}
                                            onAfterChange={fetchFilteredProperties}
                                            onMobileCloseModal={closeMobileModal}
                                            onMobileResetForm={resetFilters}
                                            setDesktopFilterBlendActive={setDesktopFilterBlendActive}
                                            currency={props.offer.currency}
                                            maxPriceInOffer={props.offer.stats?.ranges_price_max}
                                            offerType={offerType}
                                        />
                                    </MobileOptionsModal>
                                </>
                            )}
                            {isMobile && (
                                <>
                                    <SortIcon
                                        onClick={toggleMobileSortModal}
                                        size="2.4"
                                        wrapperSize="2.4"
                                        wrapperColor="transparent"
                                        dataTestid={NAVIGATION_TEST_IDS.SORT_ICON}
                                    />

                                    <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={discardModal}>
                                        <SortOptions
                                            currentValue={sortValue}
                                            setCurrentValue={setSortValue}
                                            sortOptions={sortOptions}
                                            onCancel={clearSort}
                                            onSave={saveSort}
                                        />
                                    </MobileOptionsModal>
                                </>
                            )}
                        </div>
                    </div>
                </>
            </div>
        </>
    );
};

const desktopWrapper = (props: Theme) => css`
    justify-items: stretch;
    display: none;

    ${mb(3)};

    @media (min-width: ${props.breakpoints.md}) {
        display: flex;
    }

    @media (min-width: ${props.breakpoints.lg}) {
        width: 100%;
        ${mb(0)};
    }
`;

const wrapperStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;

    ${onDesktop(css`
        flex-direction: row;
    `)}
`;

export const sortWrapper = (theme: Theme) => css`
    ${p(0)};

    ${onDesktop(css`
        flex: 0 0 20%;
    `)};

    @media (min-width: ${theme.breakpoints.lg}) {
        min-width: 200px;
    }
`;

const wrapper = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.lg}) {
        ${flex("center")};
        gap: ${calculateRemSize(1.5)};
    }
`;

const desktopFilters = css`
    width: 100%;
`;

const listHeaderStyle = (theme: Theme) => css`
    ${flex("center", "space-between")};
    gap: ${calculateRemSize(3)};
    ${w100};

    @media (min-width: ${theme.breakpoints.md}) {
        width: initial;
        flex-basis: initial;
        gap: 0;
    }
`;

const tabSwitcherButtonStyle = (theme: Theme) => css`
    ${p(2, 0.5)};
    height: initial;

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(2, 4)};
    }
`;

const tabSwitcherStyle = (theme: Theme) => css`
    gap: ${calculateRemSize(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        gap: 0;
    }
`;

const filtersPlanner3D = (theme: Theme) => css`
    display: none;
    @media (min-width: ${theme.breakpoints.md}) {
        display: inline-flex;
    }
`;
